import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import React, { FC, ReactNode, useCallback, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Task as TaskApi } from 'api/tasks';
import { Collapse, Panel, PanelHeaderTitle } from 'components/atoms/collapse';
import { IconShell } from 'components/atoms/icon-shell';
import { ViewValue } from 'components/organisms/home/filter';
import {
  InCompleteTasksEmptyState,
  NoResultEmptyState,
} from 'components/organisms/home/overview/no-result';
import { Task } from 'data/tasks';
import { useUser } from 'hooks/use-user';
import { Section } from '../section';
import { Filter, getFilteredData } from './get-render-data';
import styles from './overview.module.css';

const constructTitle = (title: ReactNode, count = 0): ReactNode => {
  return (
    <>
      {title}
      {' · '}
      {count}
    </>
  );
};

interface ListProps {
  data: Task[];
  filter: Filter;
  openTask: (record: TaskApi | Task) => void;
  clearFilter: () => void;
}

export const List: FC<ListProps> = ({
  data,
  filter,
  openTask,
  clearFilter,
}) => {
  const user = useUser();
  const memoizedGetFilteredData = useCallback(
    () => getFilteredData(data, filter, user),
    [filter, data, user],
  );

  const result = memoizedGetFilteredData();

  const activeKeysResult = result.map((r) => r.key);
  const [activeKeys, setActiveKeys] = useState(activeKeysResult);

  useDeepCompareEffect(() => {
    setActiveKeys(activeKeysResult);
  }, [activeKeysResult]);
  const handleOnChange = (selectedKeys) => {
    setActiveKeys(selectedKeys);
  };

  return (
    <Collapse
      key="list"
      className={styles.collapse}
      ghost
      defaultActiveKey={activeKeys}
      activeKey={activeKeys}
      onChange={handleOnChange}
      expandIconPosition="right"
      expandIcon={({ isActive }) => {
        return isActive ? <CaretUp16 /> : <CaretDown16 />;
      }}
    >
      {result.length > 0 ? (
        result.map((d) => {
          return d.items.length > 0 ? (
            <Panel
              key={d.key}
              showArrow
              header={
                <PanelHeaderTitle
                  icon={<IconShell size="large" icon={d.icon} />}
                  title={constructTitle(d.label, d.items.length)}
                />
              }
            >
              <Section data={d.items} onItemClick={openTask} />
            </Panel>
          ) : (
            <></>
          );
        })
      ) : filter.view === ViewValue.Incomplete ? (
        <InCompleteTasksEmptyState />
      ) : (
        <NoResultEmptyState onSecondaryClick={clearFilter} />
      )}
    </Collapse>
  );
};
